import React, { useState, useEffect } from 'react';
import { EdgeProps, getSmoothStepPath } from 'reactflow';

const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [progress, setProgress] = useState(0);

  const deleteEdge = () => {
    if (data?.onDelete) {
      data.onDelete(id);
    }
  };

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const isSelected = data?.isSelected || false;

  const handleEdgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data?.onSelect) {
      data.onSelect(id);
    }
  };

  // Animate the dot along the edge when selected
  useEffect(() => {
    let animationFrame: number;
    let startTime: number;

    if (isSelected) {
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        // Complete a full cycle in 2 seconds (back and forth)
        const cycle = 2000;
        const normalized = (elapsed % cycle) / cycle;
        // Use sine wave for smooth back and forth motion
        const value = (Math.sin(normalized * Math.PI * 2 - Math.PI / 2) + 1) / 2;
        setProgress(value);
        animationFrame = requestAnimationFrame(animate);
      };
      animationFrame = requestAnimationFrame(animate);
    } else {
      setProgress(0);
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isSelected]);

  // Calculate position on the path based on progress
  const getPointOnPath = (path: string, progress: number) => {
    // Create a temporary SVG path element
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathElement.setAttribute('d', path);
    svg.appendChild(pathElement);
    document.body.appendChild(svg);
    
    const totalLength = pathElement.getTotalLength();
    const point = pathElement.getPointAtLength(progress * totalLength);
    
    document.body.removeChild(svg);
    return point;
  };

  const [dotPosition, setDotPosition] = useState({ x: 0, y: 0 });

  // Update dot position when progress changes
  useEffect(() => {
    if (isSelected) {
      const point = getPointOnPath(edgePath, progress);
      setDotPosition({ x: point.x, y: point.y });
    }
  }, [progress, edgePath, isSelected]);

  // Calculate center for delete button
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  const getEdgeColor = () => {
    if (isSelected) return '#ff6b6b';
    if (isHovered) return '#4a90e2';
    return '#94a3b8';
  };

  const getEdgeWidth = () => {
    if (isSelected) return 4;
    if (isHovered) return 3.5;
    return 2.5;
  };

  return (
    <g>
      <path
        d={edgePath}
        style={{
          strokeWidth: getEdgeWidth(),
          stroke: getEdgeColor(),
          fill: 'none',
          ...style,
          cursor: 'pointer',
          transition: 'stroke 0.2s ease, stroke-width 0.2s ease',
        }}
        markerEnd={markerEnd}
        onClick={handleEdgeClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      {/* Invisible larger clickable area for better click detection */}
      <path
        d={edgePath}
        style={{
          strokeWidth: 20,
          stroke: 'transparent',
          fill: 'none',
          cursor: 'pointer',
        }}
        onClick={handleEdgeClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      {/* Animated dot that travels along the edge */}
      {isSelected && dotPosition.x !== 0 && dotPosition.y !== 0 && (
        <circle
          cx={dotPosition.x}
          cy={dotPosition.y}
          r={8}
          fill="#ff6b6b"
          stroke="#fff"
          strokeWidth={2}
          style={{
            filter: 'drop-shadow(0 0 6px rgba(255, 107, 107, 0.6))',
            transition: 'cx 0.05s linear, cy 0.05s linear',
          }}
        />
      )}

      {/* Delete button - only show on hover or selection */}
      {(isHovered || isSelected) && (
        <foreignObject
          x={centerX - 12}
          y={centerY - 12}
          width={24}
          height={24}
          style={{
            pointerEvents: 'all',
          }}
        >
          <div
            style={{
              background: isSelected ? '#ff6b6b' : '#fff',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              color: isSelected ? '#fff' : '#ff6b6b',
              fontWeight: 'bold',
              fontSize: '12px',
              border: isSelected ? '2px solid #fff' : '2px solid #ff6b6b',
              transition: 'all 0.2s ease',
              pointerEvents: 'all',
            }}
            onClick={(e) => {
              e.stopPropagation();
              deleteEdge();
            }}
            onMouseEnter={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              target.style.transform = 'scale(1.1)';
              target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              target.style.transform = 'scale(1)';
              target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
            }}
          >
            ✕
          </div>
        </foreignObject>
      )}
    </g>
  );
};

export default CustomEdge;